import { DashboardHeader } from "@/components/general/Dashboard/DashboardHeader";
import DashboardNavigation from "@/components/general/Dashboard/DashboardNavigation";
import { useSelectedMatch } from "@/context/matchContext";
import { Outlet, useRouterState } from "@tanstack/react-router";
import { RouteUrl } from "@/configs/router/routes";
import { PageNotFound } from "@/pages/notFound/page";
import { useQuery } from "@tanstack/react-query";
import { matchKpisQueryOptions } from "@/api/ranking";
import { tournamentsQueryOptions } from "@/api/tournaments";
import { useOnboarding } from "@/components/general/Onboarding";
import { useSelectedTeam } from "@/context/teamContext";
import { useState, createContext, useContext, useEffect } from "react";

const ViewModeContext = createContext<{
    viewMode: "average" | "total" | "line" | "period";
    setViewMode: (mode: "average" | "total" | "line" | "period") => void;
}>({
    viewMode: "average",
    setViewMode: () => {},
});

const DashboardComparisonContext = createContext<{
    selectedPlayers: Array<{ playerId: number; firstName: string; lastName: string }>;
    setSelectedPlayers: (players: Array<{ playerId: number; firstName: string; lastName: string }>) => void;
}>({
    selectedPlayers: [],
    setSelectedPlayers: () => {},
});

const PeriodDataContext = createContext<{
    periodNumbers: number[];
    setPeriodNumbers: (periods: number[]) => void;
    selectedPeriod: number | "all";
    setSelectedPeriod: (period: number | "all") => void;
}>({
    periodNumbers: [],
    setPeriodNumbers: () => {},
    selectedPeriod: "all",
    setSelectedPeriod: () => {},
});

export const useViewMode = () => useContext(ViewModeContext);
export const useDashboardComparison = () => useContext(DashboardComparisonContext);
export const usePeriodData = () => useContext(PeriodDataContext);

export default function DashboardPage() {
    const pathname = useRouterState({
        select: (state) => state.location.pathname,
    });

    const validDashboardPaths = [
        RouteUrl.Dashboard,
        RouteUrl.DashboardOverview,
        RouteUrl.DashboardPeriodData,
        RouteUrl.DashboardAnalytics,
        RouteUrl.DashboardHeatMap,
        RouteUrl.DashboardCompare,
        RouteUrl.DashboardTeamComparison,
    ];

    const isValidDashboardRoute = validDashboardPaths.includes(pathname as RouteUrl);

    if (!isValidDashboardRoute) {
        return <PageNotFound hideLayout />;
    }

    return (
        <ViewModeProvider>
            <DashboardComparisonProvider>
                <PeriodDataProvider>
                    <DashboardContent />
                </PeriodDataProvider>
            </DashboardComparisonProvider>
        </ViewModeProvider>
    );
}

function ViewModeProvider({ children }: { children: React.ReactNode }) {
    const [viewMode, setViewMode] = useState<"average" | "total" | "line" | "period">("average");

    return <ViewModeContext.Provider value={{ viewMode, setViewMode }}>{children}</ViewModeContext.Provider>;
}

function DashboardComparisonProvider({ children }: { children: React.ReactNode }) {
    const [selectedPlayers, setSelectedPlayers] = useState<
        Array<{ playerId: number; firstName: string; lastName: string }>
    >([]);

    return (
        <DashboardComparisonContext.Provider value={{ selectedPlayers, setSelectedPlayers }}>
            {children}
        </DashboardComparisonContext.Provider>
    );
}

function PeriodDataProvider({ children }: { children: React.ReactNode }) {
    const [periodNumbers, setPeriodNumbers] = useState<number[]>([]);
    const [selectedPeriod, setSelectedPeriod] = useState<number | "all">("all");

    return (
        <PeriodDataContext.Provider value={{ periodNumbers, setPeriodNumbers, selectedPeriod, setSelectedPeriod }}>
            {children}
        </PeriodDataContext.Provider>
    );
}

function DashboardContent() {
    const { matchIds } = useSelectedMatch();
    const { teamId } = useSelectedTeam();
    const { viewMode, setViewMode } = useViewMode();
    const { selectedPlayers } = useDashboardComparison();
    const { periodNumbers, selectedPeriod, setSelectedPeriod } = usePeriodData();
    const { registerBeforeAnalyticsTour } = useOnboarding();
    const { data: kpiData, isLoading } = useQuery(matchKpisQueryOptions(matchIds.length > 0 ? matchIds : [], teamId));
    const { data: tournaments, isLoading: isTournamentsLoading } = useQuery(tournamentsQueryOptions(teamId));

    useEffect(() => {
        return registerBeforeAnalyticsTour(() => setViewMode("average"));
    }, [registerBeforeAnalyticsTour, setViewMode]);

    return (
        <main className="flex flex-col p-4 h-full min-h-0" data-dashboard-loaded={!isLoading ? "true" : undefined}>
            <DashboardHeader
                kpiData={kpiData}
                isKpisLoading={isLoading}
                tournaments={tournaments}
                isTournamentsLoading={isTournamentsLoading}
            />
            <DashboardNavigation
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                selectedPlayers={selectedPlayers}
                matchIds={matchIds}
                periodNumbers={periodNumbers}
                selectedPeriod={selectedPeriod}
                onPeriodChange={setSelectedPeriod}
            />
            <div className={`mt-2 ${isLoading ? "flex-1 min-h-0 h-full" : ""}`}>
                <Outlet />
            </div>
            {!isLoading && <div className="p-2" />}
            <div className="lg:hidden py-7.5" />
        </main>
    );
}
